import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PAYMENT_METHODS, money, selectCls, btnBrand } from "@/lib/academyFormat";
import { ACADEMY } from "@/constants/testIds";

// Fix U (2026-10-13): registrar un pago de un alumno (efectivo, tarjeta del
// lector, Zelle, Cherry o transferencia). La tarjeta con link de Square llega sola.
export default function PaymentModal({ open, onOpenChange, enrollment, onSaved }) {
  const [form, setForm] = useState({ amount: "", method: "cash", paid_on: "", notes: "", is_reservation: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !enrollment) return;
    setError("");
    const needsReservation = !enrollment.steps?.reservation;
    setForm({
      amount: needsReservation ? String(enrollment.reservation_amount || "") : "",
      method: "cash", paid_on: "", notes: "", is_reservation: needsReservation,
    });
  }, [open, enrollment]);

  if (!enrollment) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    const amount = Number(form.amount);
    if (!(amount > 0)) {
      setError("Ingresá el monto");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const { data } = await api.post(`/academy/enrollments/${enrollment.id}/payments`, {
        amount, method: form.method, is_reservation: form.is_reservation, paid_on: form.paid_on || null, notes: form.notes.trim() || null,
      });
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={ACADEMY.paymentModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Registrar pago</DialogTitle>
          <DialogDescription>{enrollment.student?.name} · {enrollment.course_name} · saldo {money(enrollment.account?.balance)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Monto (USD)</Label>
              <Input type="number" min="0" step="0.01" value={form.amount} onChange={set("amount")} data-testid={ACADEMY.paymentAmountInput} />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Medio</Label>
              <select className={selectCls} value={form.method} onChange={set("method")} data-testid={ACADEMY.paymentMethodSelect}>
                {PAYMENT_METHODS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Fecha del pago</Label>
            <Input type="date" value={form.paid_on} onChange={set("paid_on")} />
            <p className="text-xs text-muted-foreground mt-1">Vacío = hoy.</p>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.is_reservation} onChange={(e) => setForm((f) => ({ ...f, is_reservation: e.target.checked }))} data-testid={ACADEMY.paymentReservationCheck} />
            Es el pago de la reserva
          </label>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Nota</Label>
            <Input value={form.notes} onChange={set("notes")} placeholder="Ej: número de Zelle, aprobación de Cherry" />
          </div>
          {error && <p className="text-sm text-destructive" data-testid={ACADEMY.modalError}>{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className={btnBrand} onClick={save} disabled={saving} data-testid={ACADEMY.paymentSaveButton}>{saving ? "Guardando..." : "Registrar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
